import React, { useEffect, useState } from 'react';
import { Search, Users } from 'lucide-react';
import { AdminPagination } from '../components/admin/AdminPagination';
import { Button } from '../components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { adminService } from '../services/adminService';
import { getApiErrorMessage } from '../utils/apiError';
import { formatMoney } from '../utils/formatMoney';

const PAGE_SIZE = 25;
const inputClass =
  'rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand-red';

function formatDate(value) {
  return value ? new Date(value).toLocaleString('th-TH') : '-';
}

function shortAddress(address) {
  if (!address) return '-';
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

export function AdminUsersPage() {
  const [draft, setDraft] = useState({ search: '', isSale: '' });
  const [filters, setFilters] = useState({ search: '', isSale: '' });
  const [data, setData] = useState(null);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    adminService
      .getUsers({
        search: filters.search || undefined,
        isSale: filters.isSale || undefined,
        limit: PAGE_SIZE,
        offset,
      })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(getApiErrorMessage(err, 'โหลดรายชื่อผู้ใช้ไม่สำเร็จ'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, offset]);

  const applyFilters = (event) => {
    event.preventDefault();
    setOffset(0);
    setFilters({ ...draft });
  };

  const toggleSale = (user) => {
    const nextIsSale = !user.isSale;
    setUpdatingId(user.id);
    setError(null);
    adminService
      .setUserSale(user.id, nextIsSale)
      .then((updated) => {
        setData((prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((row) =>
                  row.id === updated.id ? { ...row, ...updated } : row,
                ),
              }
            : prev,
        );
      })
      .catch((err) => {
        setError(getApiErrorMessage(err, 'อัปเดตสถานะ Sale ไม่สำเร็จ'));
      })
      .finally(() => setUpdatingId(null));
  };

  const total = data?.total || 0;
  const hasFilter = Boolean(filters.search || filters.isSale);

  return (
    <div className="space-y-6">
      {error ? (
        <p className="rounded-lg bg-red-50 px-4 py-3 text-sm text-brand-red">
          {error}
        </p>
      ) : null}

      {hasFilter ? (
        <Card>
          <CardContent className="pt-6 text-sm text-gray-600">
            ยอดตามตัวกรอง:{' '}
            <span className="font-bold text-brand-red">
              {formatMoney(data?.filteredBalance ?? 0)}
            </span>{' '}
            จาก {total} บัญชี
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            ผู้ใช้ / Sale — ยอดแต่ละบัญชี
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            onSubmit={applyFilters}
            className="flex flex-wrap items-end gap-3"
          >
            <label className="flex min-w-[220px] flex-1 flex-col gap-1 text-sm">
              ค้นหา
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  className={`${inputClass} w-full pl-9`}
                  placeholder="username / wallet / code / id"
                  value={draft.search}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, search: event.target.value }))
                  }
                />
              </div>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              สถานะ Sale
              <select
                className={inputClass}
                value={draft.isSale}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, isSale: event.target.value }))
                }
              >
                <option value="">ทั้งหมด</option>
                <option value="true">Sale</option>
                <option value="false">ปกติ</option>
              </select>
            </label>
            <Button type="submit" disabled={loading}>
              ค้นหา
            </Button>
          </form>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="text-xs uppercase text-gray-500">
                <tr>
                  <th className="py-2 pr-3">ID</th>
                  <th className="py-2 pr-3">Username</th>
                  <th className="py-2 pr-3">Wallet</th>
                  <th className="py-2 pr-3">Referral</th>
                  <th className="py-2 pr-3 text-right">Balance (ถอนได้)</th>
                  <th className="py-2 pr-3">Sale</th>
                  <th className="py-2 pr-3">สมัครเมื่อ</th>
                  <th className="py-2 pr-3">จัดการ</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-red-50">
                {loading && !data ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500">
                      กำลังโหลด...
                    </td>
                  </tr>
                ) : null}
                {!loading && data?.items?.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gray-500">
                      ไม่พบผู้ใช้
                    </td>
                  </tr>
                ) : null}
                {data?.items?.map((user) => (
                  <tr key={user.id}>
                    <td className="py-3 pr-3 font-medium text-gray-700">
                      {user.id}
                    </td>
                    <td className="py-3 pr-3">{user.username || '-'}</td>
                    <td className="py-3 pr-3 font-mono text-xs">
                      {shortAddress(user.walletAddress)}
                    </td>
                    <td className="py-3 pr-3">{user.referralCode || '-'}</td>
                    <td className="py-3 pr-3 text-right font-bold text-brand-red tabular-nums">
                      {formatMoney(user.balance)}
                    </td>
                    <td className="py-3 pr-3">
                      <span
                        className={`rounded-full px-2 py-1 text-xs font-semibold ${
                          user.isSale
                            ? 'bg-brand-gold/20 text-brand-gold'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {user.isSale ? 'Sale 10%' : 'ปกติ 3%'}
                      </span>
                    </td>
                    <td className="py-3 pr-3 text-gray-600">
                      {formatDate(user.createdAt)}
                    </td>
                    <td className="py-3 pr-3">
                      <Button
                        size="sm"
                        variant={user.isSale ? 'outline' : 'gold'}
                        disabled={updatingId === user.id || loading}
                        onClick={() => toggleSale(user)}
                      >
                        {updatingId === user.id
                          ? 'กำลังบันทึก...'
                          : user.isSale
                            ? 'ยกเลิก Sale'
                            : 'ตั้งเป็น Sale'}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <AdminPagination
            offset={offset}
            limit={PAGE_SIZE}
            total={total}
            loading={loading}
            onOffsetChange={setOffset}
          />
        </CardContent>
      </Card>
    </div>
  );
}
